import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../layouts/MainLayout';
import StandardFilterPanel from '../../components/hr/StandardFilterPanel';
import {
  Card,
  Button,
  Table,
  Space,
  Tag,
  message,
  Modal,
  Dropdown,
  Avatar,
  Popconfirm,
  Input,
  Tooltip,
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  EyeOutlined,
  EditOutlined,
  TeamOutlined,
  DownOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  StopOutlined,
  MessageOutlined,
} from '@ant-design/icons';
import { getLast30DaysRange } from '../../utils/dateUtils';
import {
  buildCommonDatePresets,
  formatJalaliDateForApi,
  parseJalaliDateRangeValue,
} from '../../utils/filterDateUtils';
import { clientsApi } from '../../utils/api';
import { TableSkeleton } from '../../components/SkeletonLoaders';
import PermissionGate from '../../components/PermissionGate';
import { useResourcePermissions } from '../../hooks/useResourcePermissions';
import { getImagePath } from '../../utils/imageHelper';
import { useListReturnState } from '../../hooks/useListReturnState';
import {
  createEntityBreadcrumbTrail,
  persistBreadcrumbTrail,
  BREADCRUMB_TRAIL_KEY,
} from '../../utils/breadcrumbTrail';
import { BULK_ACTION_BAR_CLASS, BULK_ACTION_BAR_TEXT_CLASS } from '../../constants/ui';
import useRemoteClientOptions from '../../hooks/useRemoteClientOptions';

const statusMeta = {
  active: { text: 'فعال', color: 'green' },
  deactive: { text: 'غیرفعال', color: 'default' },
};

const Clients = () => {
  const navigate = useNavigate();
  const { canEdit, canDelete, canEditRecord, canDeleteRecord } = useResourcePermissions('clients');

  const defaultRange = useMemo(() => getLast30DaysRange('jYYYY/jMM/jDD'), []);
  const [filters, setFilters] = useState({
    startDate: defaultRange.startDate,
    endDate: defaultRange.endDate,
    status: 'all',
    client: 'all',
    category_id: 'all',
    sub_category_id: 'all',
    project_id: 'all',
    contract_type_id: 'all',
    verification: 'all',
  });

  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 10,
    total: 0,
    last_page: 1,
  });
  const [filterOptions, setFilterOptions] = useState({
    clients: [],
    categories: [],
    subcategories: [],
    projects: [],
    contracts: [],
  });
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [smsModalOpen, setSmsModalOpen] = useState(false);
  const [smsMessage, setSmsMessage] = useState('');
  const [smsTargetIds, setSmsTargetIds] = useState([]);
  const [smsLoading, setSmsLoading] = useState(false);

  const captureCurrentListState = useCallback(
    () => ({
      filters,
      searchText,
      pagination: {
        current_page: pagination.current_page,
        per_page: pagination.per_page,
      },
    }),
    [filters, searchText, pagination.current_page, pagination.per_page],
  );

  const { navigateToDetail } = useListReturnState(captureCurrentListState, (restored) => {
    if (restored.filters) setFilters(restored.filters);
    if (restored.searchText != null) setSearchText(restored.searchText);
    if (restored.pagination) {
      setPagination((prev) => ({
        ...prev,
        current_page: restored.pagination.current_page || 1,
        per_page: restored.pagination.per_page || prev.per_page,
      }));
    }
  });

  const navigateToClientDetail = useCallback(
    (row, pathSuffix = '?tab=deals') => {
      const entityPath = `/clients/${row.id}${pathSuffix}`;
      const trail = createEntityBreadcrumbTrail(
        '/clients',
        'مشتریان',
        entityPath,
        row.name || row.client_name || `مشتری #${row.id}`,
      );
      persistBreadcrumbTrail(`/clients/${row.id}`, trail);
      persistBreadcrumbTrail(entityPath, trail);
      navigateToDetail(entityPath, { [BREADCRUMB_TRAIL_KEY]: trail });
    },
    [navigateToDetail],
  );

  const subcategoriesFiltered = useMemo(() => {
    if (filters.category_id === 'all') return filterOptions.subcategories || [];
    return (filterOptions.subcategories || []).filter(
      (s) => String(s.category_id) === String(filters.category_id)
    );
  }, [filterOptions.subcategories, filters.category_id]);

  useEffect(() => {
    if (filters.category_id !== 'all' && filters.sub_category_id !== 'all') {
      const ok = subcategoriesFiltered.some((s) => String(s.id) === String(filters.sub_category_id));
      if (!ok) {
        setFilters((prev) => ({ ...prev, sub_category_id: 'all' }));
      }
    }
  }, [filters.category_id, filters.sub_category_id, subcategoriesFiltered]);

  const fetchClients = async (page = pagination.current_page, perPage = pagination.per_page) => {
    setLoading(true);
    try {
      // هنگام جستجو، بازه تاریخ حذف می‌شود تا در تمام آیتم‌ها جستجو شود
      const hasSearch = Boolean(searchText && searchText.trim());
      const res = await clientsApi.getAll({
        startDate: hasSearch ? undefined : formatJalaliDateForApi(filters.startDate),
        endDate: hasSearch ? undefined : formatJalaliDateForApi(filters.endDate),
        status: filters.status,
        client: filters.client,
        category_id: filters.category_id,
        sub_category_id: filters.sub_category_id,
        project_id: filters.project_id,
        contract_type_id: filters.contract_type_id,
        verification: filters.verification,
        search: searchText || undefined,
        page,
        per_page: perPage,
      });
      setClients(res?.clients || []);
      setPagination(
        res?.pagination || { current_page: page, per_page: perPage, total: 0, last_page: 1 }
      );
      const f = res?.filters || {};
      setFilterOptions({
        categories: f.categories || [],
        subcategories: f.subcategories || [],
        projects: f.projects || [],
        contracts: f.contracts || [],
      });
    } catch (e) {
      message.error('خطا در دریافت لیست مشتریان');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients(pagination.current_page, pagination.per_page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current_page, pagination.per_page]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPagination((prev) => ({ ...prev, current_page: 1 }));
      fetchClients(1, pagination.per_page);
    }, 450);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, searchText]);

  useEffect(() => {
    setSelectedRowKeys([]);
  }, [filters]);

  const handleFilterChange = (key, value) => {
    if (key === 'dateRange') {
      const { startDate, endDate } = parseJalaliDateRangeValue(value);
      setFilters((prev) => ({ ...prev, startDate, endDate }));
      return;
    }
    const normalized = value === null || value === undefined || value === '' ? 'all' : value;
    setFilters((prev) => {
      const next = { ...prev, [key]: normalized };
      if (key === 'category_id') next.sub_category_id = 'all';
      return next;
    });
  };

  const handleResetFilters = () => {
    const dr = getLast30DaysRange('jYYYY/jMM/jDD');
    setFilters({
      startDate: dr.startDate,
      endDate: dr.endDate,
      status: 'all',
      client: 'all',
      category_id: 'all',
      sub_category_id: 'all',
      project_id: 'all',
      contract_type_id: 'all',
      verification: 'all',
    });
    setPagination((prev) => ({ ...prev, current_page: 1 }));
    setSearchText('');
  };

  const handleBulkDelete = () => {
    Modal.confirm({
      title: <span style={{ fontFamily: 'CustomFont' }}>حذف مشتریان</span>,
      content: (
        <span style={{ fontFamily: 'CustomFont' }}>
          آیا از حذف {selectedRowKeys.length} مورد انتخاب‌شده اطمینان دارید؟
        </span>
      ),
      okText: 'بله',
      cancelText: 'لغو',
      okButtonProps: {
        danger: true,
        style: { backgroundColor: '#dc2626', borderColor: '#dc2626', color: '#fff' },
      },
      onOk: async () => {
        setBulkLoading(true);
        try {
          await clientsApi.applyQuickAction('delete', selectedRowKeys);
          message.success('حذف انجام شد');
          setSelectedRowKeys([]);
          fetchClients(pagination.current_page, pagination.per_page);
        } catch (e) {
          message.error('خطا در حذف');
        } finally {
          setBulkLoading(false);
        }
      },
    });
  };

  const handleBulkStatus = (newStatus) => {
    const label = statusMeta[newStatus]?.text || newStatus;
    Modal.confirm({
      title: <span style={{ fontFamily: 'CustomFont' }}>تغییر وضعیت</span>,
      content: (
        <span style={{ fontFamily: 'CustomFont' }}>
          تغییر وضعیت {selectedRowKeys.length} مشتری به «{label}»؟
        </span>
      ),
      okText: 'بله',
      cancelText: 'لغو',
      okButtonProps: {
        style: { backgroundColor: '#2563eb', borderColor: '#2563eb', color: '#fff' },
      },
      onOk: async () => {
        setBulkLoading(true);
        try {
          await clientsApi.applyQuickAction('change-status', selectedRowKeys, newStatus);
          message.success('وضعیت بروزرسانی شد');
          setSelectedRowKeys([]);
          fetchClients(pagination.current_page, pagination.per_page);
        } catch (e) {
          message.error('خطا در تغییر وضعیت');
        } finally {
          setBulkLoading(false);
        }
      },
    });
  };

  const openSmsModal = (ids) => {
    const targetIds = (Array.isArray(ids) ? ids : [ids]).filter(Boolean);
    if (!targetIds.length) {
      message.warning('لطفاً حداقل یک مشتری را انتخاب کنید');
      return;
    }
    setSmsTargetIds(targetIds);
    setSmsMessage('');
    setSmsModalOpen(true);
  };

  const handleSmsSubmit = async () => {
    const text = String(smsMessage || '').trim();
    if (!text) {
      message.warning('لطفاً متن پیامک را وارد کنید');
      return;
    }
    setSmsLoading(true);
    try {
      await clientsApi.applyQuickAction('send-sms', smsTargetIds, { message: text });
      message.success(
        smsTargetIds.length > 1 ? 'پیامک گروهی ارسال شد' : 'پیامک ارسال شد',
      );
      setSmsModalOpen(false);
      setSmsMessage('');
      setSmsTargetIds([]);
      if (smsTargetIds.length > 1) {
        setSelectedRowKeys([]);
      }
    } catch (e) {
      message.error(e?.message || 'خطا در ارسال پیامک');
    } finally {
      setSmsLoading(false);
    }
  };

  const bulkItems = [
    {
      key: 'send-sms',
      label: 'ارسال گروهی پیامک',
      icon: <MessageOutlined />,
      onClick: () => openSmsModal(selectedRowKeys),
    },
    ...(canDelete
      ? [
          { type: 'divider' },
          {
            key: 'del',
            label: 'حذف',
            icon: <DeleteOutlined />,
            danger: true,
            onClick: handleBulkDelete,
          },
        ]
      : []),
    ...(canEdit
      ? [
          { type: 'divider' },
          {
            key: 'st-active',
            label: 'فعال کردن',
            icon: <CheckCircleOutlined className="text-green-600" />,
            onClick: () => handleBulkStatus('active'),
          },
          {
            key: 'st-deactive',
            label: 'غیرفعال کردن',
            icon: <StopOutlined />,
            onClick: () => handleBulkStatus('deactive'),
          },
        ]
      : []),
  ];

  const clientRemote = useRemoteClientOptions({
    selectedValue: filters.client === 'all' ? null : filters.client,
    includeInactive: true,
  });

  const categoryOptions = useMemo(
    () =>
      (filterOptions.categories || []).map((c) => ({
        value: c.id,
        label: c.category_name || c.name,
      })),
    [filterOptions.categories]
  );

  const subcategoryOptions = useMemo(
    () =>
      subcategoriesFiltered.map((s) => ({
        value: s.id,
        label: s.category_name || s.name,
      })),
    [subcategoriesFiltered]
  );

  const projectOptions = useMemo(
    () =>
      (filterOptions.projects || []).map((p) => ({
        value: p.id,
        label: p.project_name || p.name,
      })),
    [filterOptions.projects]
  );

  const contractOptions = useMemo(
    () =>
      (filterOptions.contracts || []).map((c) => ({
        value: c.id,
        label: c.name || c.title || `قرارداد ${c.id}`,
      })),
    [filterOptions.contracts]
  );

  const quickFilters = [
    {
      key: 'status',
      type: 'select',
      label: 'وضعیت',
      placeholder: 'همه وضعیت‌ها',
      value: filters.status === 'all' ? undefined : filters.status,
      options: [
        { value: 'active', label: 'فعال' },
        { value: 'deactive', label: 'غیرفعال' },
      ],
      showSearch: false,
    },
    {
      key: 'client',
      type: 'select',
      label: 'مشتری',
      placeholder: 'همه مشتریان',
      value: filters.client === 'all' ? undefined : filters.client,
      options: clientRemote.options,
      ...clientRemote.selectProps,
    },
    {
      key: 'dateRange',
      type: 'dateRange',
      label: 'بازه تاریخ',
      placeholder: 'از تاریخ — تا تاریخ',
      value: filters.startDate && filters.endDate ? [filters.startDate, filters.endDate] : undefined,
    },
    {
      key: 'datePreset',
      type: 'datePreset',
      label: 'بازه سریع',
      placeholder: 'بازه سریع',
      options: buildCommonDatePresets(),
      onPreset: (range) => {
        setFilters((prev) => ({ ...prev, ...range }));
        setPagination((prev) => ({ ...prev, current_page: 1 }));
      },
    },
  ];

  const advancedFilters = [
    {
      key: 'category_id',
      type: 'select',
      label: 'دسته',
      placeholder: 'همه دسته‌ها',
      value: filters.category_id === 'all' ? undefined : filters.category_id,
      options: categoryOptions,
      showSearch: true,
    },
    {
      key: 'sub_category_id',
      type: 'select',
      label: 'زیردسته',
      placeholder: 'همه زیردسته‌ها',
      value: filters.sub_category_id === 'all' ? undefined : filters.sub_category_id,
      options: subcategoryOptions,
      showSearch: true,
    },
    {
      key: 'project_id',
      type: 'select',
      label: 'پروژه',
      placeholder: 'همه پروژه‌ها',
      value: filters.project_id === 'all' ? undefined : filters.project_id,
      options: projectOptions,
      showSearch: true,
    },
    {
      key: 'contract_type_id',
      type: 'select',
      label: 'نوع قرارداد',
      placeholder: 'همه',
      value: filters.contract_type_id === 'all' ? undefined : filters.contract_type_id,
      options: contractOptions,
      showSearch: true,
    },
    {
      key: 'verification',
      type: 'select',
      label: 'تایید مدیر',
      placeholder: 'همه',
      value: filters.verification === 'all' ? undefined : filters.verification,
      options: [
        { value: '1', label: 'تایید شده' },
        { value: '0', label: 'تایید نشده' },
      ],
    },
  ];

  const columns = [
    {
      title: 'مشتری',
      key: 'name',
      render: (_, row) => (
        <Space>
          <button
            type="button"
            onClick={() => navigateToClientDetail(row)}
            className="flex items-center gap-2 bg-transparent border-0 p-0 cursor-pointer hover:opacity-80"
          >
            <Avatar src={getImagePath(row.image_url || row.image)} size={40}>
              {(row.name || '?').charAt(0)}
            </Avatar>
            <span style={{ fontFamily: 'CustomFont', fontWeight: 600 }} className="text-gray-800 dark:text-gray-100 hover:text-blue-600">
              {row.name || '—'}
            </span>
          </button>
        </Space>
      ),
    },
    {
      title: 'ایمیل',
      dataIndex: 'email',
      key: 'email',
      ellipsis: true,
      render: (t) => <span style={{ fontFamily: 'CustomFont' }}>{t || '—'}</span>,
    },
    {
      title: 'موبایل',
      dataIndex: 'mobile',
      key: 'mobile',
      width: 130,
      render: (t) => <span style={{ fontFamily: 'CustomFont' }}>{t || '—'}</span>,
    },
    {
      title: 'تاریخ ایجاد',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 130,
      render: (date) => (
        <span style={{ fontFamily: 'CustomFont' }}>
          {date ? new Date(date).toLocaleDateString('fa-IR') : '—'}
        </span>
      ),
    },
    {
      title: 'وضعیت',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (s) => {
        const m = statusMeta[s] || { text: s, color: 'default' };
        return <Tag color={m.color}>{m.text}</Tag>;
      },
    },
    {
      title: 'عملیات',
      key: 'actions',
      width: 240,
      render: (_, row) => (
        <Space>
          <Tooltip title="مشاهده">
            <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => navigateToClientDetail(row)} />
          </Tooltip>
          <Tooltip title="ارسال پیامک">
            <Button
              type="link"
              size="small"
              icon={<MessageOutlined />}
              onClick={() => openSmsModal([row.id])}
            />
          </Tooltip>
          {canEditRecord(row) ? (
            <Tooltip title="ویرایش">
              <Button type="link" size="small" icon={<EditOutlined />} onClick={() => navigateToClientDetail(row, '/edit')} />
            </Tooltip>
          ) : null}
          {canDelete && (!canDeleteRecord || canDeleteRecord(row)) ? (
            <Popconfirm
              title="حذف مشتری"
              description="آیا از حذف این مشتری اطمینان دارید؟"
              okText="بله"
              cancelText="خیر"
              okButtonProps={{ danger: true }}
              onConfirm={async () => {
                try {
                  await clientsApi.applyQuickAction('delete', [row.id]);
                  message.success('مشتری حذف شد');
                  fetchClients(pagination.current_page, pagination.per_page);
                } catch {
                  message.error('خطا در حذف مشتری');
                }
              }}
            >
              <Tooltip title="حذف">
                <Button type="link" size="small" danger icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          ) : null}
        </Space>
      ),
    },
  ];

  return (
    <MainLayout>
      <div className="" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" bodyStyle={{ padding: 0 }}>
          <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-green-50 dark:bg-green-900/20 flex items-center justify-center">
                <TeamOutlined className="text-green-600 dark:text-green-400 text-xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0">مشتریان</h1>
                {/* <p className="text-gray-500 dark:text-gray-400 text-sm m-0">مدیریت مشتریان</p> */}
              </div>
            </div>
            <Space wrap>
              <Button icon={<ReloadOutlined />} onClick={() => fetchClients(pagination.current_page, pagination.per_page)} loading={loading}>
                بروزرسانی
              </Button>
              <PermissionGate permission="add_clients">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  className="bg-green-600 hover:bg-green-700 border-0"
                  onClick={() => navigate('/clients/add')}
                >
                  مشتری جدید
                </Button>
              </PermissionGate>
            </Space>
          </div>

          <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl">
            {selectedRowKeys.length > 0 ? (
              <div className={BULK_ACTION_BAR_CLASS}>
                <span className={BULK_ACTION_BAR_TEXT_CLASS} style={{ fontFamily: 'CustomFont' }}>
                  {selectedRowKeys.length} مورد انتخاب شده
                </span>
                <Dropdown menu={{ items: bulkItems }} trigger={['click']} disabled={bulkLoading || smsLoading}>
                  <Button
                    type="primary"
                    loading={bulkLoading || smsLoading}
                    size="middle"
                    className="border-0"
                    icon={<DownOutlined />}
                    style={{ fontFamily: 'CustomFont', backgroundColor: '#f97316', color: '#fff' }}
                  >
                    عملیات گروهی
                  </Button>
                </Dropdown>
              </div>
            ) : null}

            <div className="mb-6">
              <StandardFilterPanel
                searchValue={searchText}
                onSearchChange={setSearchText}
                onSearch={() => fetchClients(1, pagination.per_page)}
                searchPlaceholder="جستجو نام، ایمیل یا موبایل مشتری..."
                quickFilters={quickFilters}
                advancedFilters={advancedFilters}
                onFilterChange={handleFilterChange}
                onResetFilters={handleResetFilters}
                totalCount={pagination.total}
                showAdvancedFilters
                showTotalCount
              />
            </div>

            {loading ? (
              <TableSkeleton rows={8} columns={5} />
            ) : (
              <Table
                rowKey="id"
                columns={columns}
                dataSource={clients}
                rowSelection={{
                  selectedRowKeys,
                  onChange: setSelectedRowKeys,
                }}
                pagination={{
                  current: pagination.current_page,
                  pageSize: pagination.per_page,
                  total: pagination.total,
                  showSizeChanger: true,
                  showTotal: (total, range) => (
                    <span style={{ fontFamily: 'CustomFont' }}>
                      نمایش {range[0]} تا {range[1]} از {total} ردیف
                    </span>
                  ),
                  onChange: (page, pageSize) => {
                    setPagination((prev) => ({
                      ...prev,
                      current_page: page,
                      per_page: pageSize,
                    }));
                  },
                }}
                scroll={{ x: 900 }}
                locale={{
                  emptyText: (
                    <div style={{ fontFamily: 'CustomFont', padding: '40px 0', color: '#999' }}>
                      مشتری‌ای یافت نشد
                    </div>
                  ),
                }}
                className="custom-table"
                rowClassName="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
              />
            )}
          </div>
        </Card>

        <Modal
          title={
            <span style={{ fontFamily: 'CustomFont' }}>
              {smsTargetIds.length > 1 ? 'ارسال گروهی پیامک' : 'ارسال پیامک'}
            </span>
          }
          open={smsModalOpen}
          onCancel={() => {
            setSmsModalOpen(false);
            setSmsMessage('');
            setSmsTargetIds([]);
          }}
          onOk={handleSmsSubmit}
          okText="ارسال"
          cancelText="لغو"
          confirmLoading={smsLoading}
          destroyOnClose
        >
          <div className="py-2">
            <label
              className="block mb-2 text-sm text-gray-700 dark:text-gray-300"
              style={{ fontFamily: 'CustomFont' }}
            >
              متن پیامک ({smsTargetIds.length} مشتری)
            </label>
            <Input.TextArea
              value={smsMessage}
              onChange={(e) => setSmsMessage(e.target.value)}
              rows={4}
              placeholder="متن پیامک را وارد کنید"
              style={{ fontFamily: 'CustomFont' }}
            />
          </div>
        </Modal>
      </div>
    </MainLayout>
  );
};

export default Clients;
